import { useEffect, useState } from 'react';
import { Col, Container, Row, Tab, Tabs } from 'react-bootstrap';
import Router from 'next/router';
import SignIn from '@bigyellow/components/SignIn/SignIn';
import FindUserWithDetails from '@bigyellow/components/SignIn/FindUserWithDetails';
import { clearSession, getItem, getSessionItem, removeItem, removeSessionItem, setItem, setSessionItem, } from '@bigyellow/libs/storage';
import { withStore } from '@bigyellow/store';
import withMaintenanceRedirect from '@bigyellow/components/SiteMaintenance/withMaintenanceRedirect';
import { redirect } from '@bigyellow/libs/redirect';
import PATH, { HOME } from '@bigyellow/routes';
import VerifyEmailCode from '@bigyellow/components/SignIn/VerifyEmail';
import CreatePasswordForm from '@bigyellow/components/SignIn/CreatePassword';
import AuthAppVerification from '@bigyellow/components/SignIn/AuthAppVerification';
import SelectTwoStepVerification, {
    TwoFAMethod,
} from '@bigyellow/components/SignIn/SelectTwoStepVerification';
import AuthByMobileNumber from '@bigyellow/components/SignIn/AuthByMobileNumber';
import { email_check, email_send } from '@bigyellow/api/register';
import NotAllowedToCheckinMessage from '@bigyellow/components/NotAllowedToCheckinMessage';
import { CheckinIntro } from '@bigyellow/components/SignIn/CheckinIntro';
import { handle_api_exception } from '@bigyellow/api/exception';
import { setCurrentCustomer } from '@bigyellow/libs/customer';

const SignUpState = {
    Checkin: 'checkin',
    VerifyEmailCode: 'verifyEmailCode',
    CreatePassword: 'createPassword',
    SelectTwoStepVerification: '2StepVerification',
    AuthByMobileNumber: 'mobileVerification',
    VerifyMobileCode: 'verifyMobileCode',
    AuthAppVerification: 'authAppVerification',
    VerifyAuthAppCode: 'verifyAuthAppCode',
    Complete: 'complete',
    NotAllowedToCheckin: 'notAllowedToCheckin',
};

const TabState = {
    SignIn: 'signin',
    SignUp: 'checkin',
};

const Index = ({ store }) => {
    const [tabKey, setTabKey] = useState(TabState.SignIn);
    const [user, setUser] = useState({
        id: 0,
        alreadyLinked: false,
        customerId: '',
        mobileNumber: '',
        onlineId: null,
        email: '',
        customerNumber: '',
        unitNumber: '',
        password: '',
        signUpState: SignUpState.Checkin,
    });
    const [emailDebugCode, setEmailDebugCode] = useState('');
    const [submitting, setSubmitting] = useState(false);

    const notify = store?.get('notify');
    const http_client = store ? store.get('http_client') : null;

    useEffect(() => {
        Router.prefetch('/forgot-password');
        handleSignOutReason();
        handleFlashMessage();
        handleUserAlreadySignedIn();
        handleHashChange();
        loadRegisteringUser();
    }, []);


    useEffect(() => {
        const handleFocus = () => {
            let registeringUser = getSessionItem('registeringUser');
            if (registeringUser) {
                validateRegisteringUser(registeringUser);
            }
        };
        window.addEventListener('focus', handleFocus);
        return () => window.removeEventListener('focus', handleFocus);
    }, []);

    useEffect(() => {
        if (isUserRegistering(user)) {
            setSessionItem('registeringUser', user);
        } else if (user.signUpState === SignUpState.Complete) {
            completeRegistration();
        }
    }, [user]);
    
    const endRegistrationSession =()=>{
        removeSessionItem('registeringUser');
        
    }

    const handleSignOutReason = () => {
        getItem('signout-reason').then((res) => {
            removeItem('signout-reason').then(() => res && notify?.(res));
        });
    };

    const handleFlashMessage = () => {
        getItem('flashmessage').then((res) => {
            if (res?.message) {
                const { message, type } = res;
                removeItem('flashmessage').then(() => notify?.(message, { type }));
            }
        });
    };

    const handleUserAlreadySignedIn = () => {
        getItem('user').then((res) =>{
            if(res !== null) 
            {
                clearSession();
                redirect(HOME);
            }
        });
    };

    const handleHashChange = () => {
        if (location.hash === '#checkin') setTabKey(TabState.SignUp);
    };

    const loadRegisteringUser = () => {
        let registeringUser = getSessionItem('registeringUser');
        if (registeringUser) {
            setUser((prev) => ({ ...prev, ...registeringUser }));
            validateRegisteringUser(registeringUser);
        }
    };

    const validateRegisteringUser = async (user) => {
        if (!user || !user.email) return;

        if (!isUserRegistering(user) || user.signUpState === SignUpState.VerifyEmailCode) return;

        const lastValidationTime = await getItem('timeSinceLastValidation');
        if (lastValidationTime && Date.now() - lastValidationTime < 5000) return;

        const isVerified = await isEmailVerified(user.email);
        if (!isVerified) {
            setUser({ ...user, signUpState: SignUpState.VerifyEmailCode });
            notify?.('Your code has expired. Please request a new code.', { type: 'error' });
            setEmailDebugCode('');
        }
        await setItem('timeSinceLastValidation', Date.now());
    };

    const isUserRegistering = (user) => ![SignUpState.Checkin, SignUpState.Complete].includes(user.signUpState);

    const isEmailVerified = async (email) => {
        const { data: { data: { verified } } } = await email_check(http_client, { email });
        return verified;
    };

    const completeRegistration = () => {
        endRegistrationSession();
        setCurrentCustomer(user).then(() => {
            setItem('customer_is_new', true).then(() => redirect(HOME));
        });
    };

    const requestEmailVerificationCode = (email, customerId) => {
        if (submitting) return;
        setSubmitting(true);
        email_send(http_client, { email, customerId })
            .then(({ data: { debug } }) => debug && setEmailDebugCode(debug.code))
            .catch((error) => notify?.(handle_api_exception(error), { type: 'error' }))
            .finally(() => setSubmitting(false));
    };


    const onUserFound = async (code, newUser, checkinDetails) => {
        newUser.email = checkinDetails.email;
        newUser.unitNumber = checkinDetails.unitNumber;
        newUser.customerNumber = checkinDetails.customerNumber;
        // If the user already exists, we need to link the reservation to the user
        if (newUser.alreadyLinked === false && newUser.onlineId !== null) {
            store.get('notify')(
                'An account with this email already exists, please enter your details below to login'
            );
            setTabKey(TabState.SignIn);
            setUser({ ...user, signUpState: SignUpState.Checkin });
            return;
        }

        if (code === 200 && newUser.alreadyLinked === false) {
            setItem('customer_is_new', true).then(() => {});
            try {
                let verified = await isEmailVerified(newUser.email);
                if (!verified) {
                    const emailSendResponse = await email_send(http_client, {
                        email: newUser.email,
                        customerId: newUser.customerId,
                    });
                    const { debug, code } = emailSendResponse.data;
                    if (debug) setEmailDebugCode(debug?.code);

                    if (code === 201) {
                        setUser({
                            ...user,
                            ...newUser,
                            signUpState: SignUpState.VerifyEmailCode,
                        });
                        if (document?.cookie) {
                            document.cookie =
                                'ld=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;';
                        }
                    }
                } else {
                    setUser({
                        ...user,
                        ...newUser,
                        signUpState: SignUpState.CreatePassword,
                    });
                }
            } catch (error) {
                notify(handle_api_exception(error), { type: 'error' });
            }
        } else {
            const message =
                'You have already created your account. Please sign in as an existing customer.';
            notify(message, { type: 'error' });
            setUser({ ...user, signUpState: SignUpState.Checkin });
            setTabKey(TabState.SignIn);
        }
    };


    const onPasswordCreated = (newPassword) => {
        setUser({
            ...user,
            password: newPassword,
            signUpState: SignUpState.SelectTwoStepVerification,
        });
    };
    const onEmailVerified = () => {
        setUser({ ...user, signUpState: SignUpState.CreatePassword });
    };
    const on2StepVerificationSelected = (verificationType) => {
        if (verificationType === TwoFAMethod.Sms) {
            setUser({ ...user, signUpState: SignUpState.AuthByMobileNumber });
        } else {
            setUser({ ...user, signUpState: SignUpState.AuthAppVerification });
        }
    };
    const on2StepVerificationCodeVerified = (newUser) => {
        setUser({ ...user, ...newUser, signUpState: SignUpState.Complete });
    };
    const onCancel2StepVerification = () => {
        setUser({
            ...user,
            signUpState: SignUpState.SelectTwoStepVerification,
        });
    };
    const onEmailVerificationCancelled = () => {
        endRegistrationSession();
        setUser({ ...user, signUpState: SignUpState.Checkin });
    };

    const Content = () => {
        switch (user.signUpState) {
            case SignUpState.VerifyEmailCode: {
                return (
                    <VerifyEmailCode
                        email={user.email}
                        onSendEmailVerificationCode={() => {
                            requestEmailVerificationCode(user.email, user.customerId);
                        }}
                        inDebugCode={emailDebugCode}
                        OnSuccess={onEmailVerified}
                        isDisabled={submitting}
                        OnCancel={onEmailVerificationCancelled}
                    />
                );
            }
            case SignUpState.CreatePassword: {
                return (
                    <CreatePasswordForm user={user} OnSuccess={onPasswordCreated} />
                );
            }
            case SignUpState.SelectTwoStepVerification: {
                return (
                    <SelectTwoStepVerification
                        user={user}
                        onChange={on2StepVerificationSelected}
                    />
                );
            }
            case SignUpState.AuthByMobileNumber: {
                return (
                    <AuthByMobileNumber
                        user={user}
                        onComplete={on2StepVerificationCodeVerified}
                        onCancel={onCancel2StepVerification}
                    />
                );
            }
            case SignUpState.AuthAppVerification: {
                return (
                    <AuthAppVerification
                        user={user}
                        onComplete={on2StepVerificationCodeVerified}
                        onCancel={onCancel2StepVerification}
                    />
                );
            }
            case SignUpState.NotAllowedToCheckin: {
                return <NotAllowedToCheckinMessage />;
            }
            default: {
                if (tabKey === TabState.SignUp && user.signUpState === SignUpState.Complete) {
                    return <></>;
                }

                return (
                    <Tabs
                        fill
                        variant={`tabs`}
                        activeKey={tabKey}
                        transition={false}
                        id="signin-checkin-tabs"
                        className="mb-4"
                        onSelect={setTabKey}
                    >
                        <Tab eventKey="signin" title="My Account" className={`mt-3`}>
                            <SignIn tabHandler={setTabKey} />
                        </Tab>
                        <Tab eventKey="checkin" title="Create account">
                            <CheckinIntro />
                            <FindUserWithDetails
                                OnComplete={onUserFound}
                                tabHandler={setTabKey}
                            />
                        </Tab>
                    </Tabs>
                );
            }
        }
    };
    const Header = () => {
        return (
            <div className="logo text-center mb-5">
                <a href={`/`}>
                    <img
                        src={`${PATH}/assets/img/logo.svg`}
                        alt="BY"
                        className="large-logo"
                        width={120}
                    />
                </a>
            </div>
        );
    };

    return (
        <>
            <Container className={`auth-main-content`}>
                <Row>
                    <Col>
                        <Header />
                        <Content />
                    </Col>
                </Row>
            </Container>
        </>
    );
};

export default withMaintenanceRedirect(withStore(Index));
